import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import type { Locale } from '@nexops/shared';

const DATE_LOCALES = { pl, en: enUS } as const;

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(value) : value;
  return isValid(d) ? d : null;
}

export function formatDate(value: string | Date | null | undefined, locale: Locale, pattern = 'd MMM yyyy'): string {
  const d = toDate(value);
  return d ? format(d, pattern, { locale: DATE_LOCALES[locale] }) : '—';
}

export function formatRelative(value: string | Date | null | undefined, locale: Locale): string {
  const d = toDate(value);
  return d ? formatDistanceToNowStrict(d, { addSuffix: true, locale: DATE_LOCALES[locale] }) : '—';
}

const intlLocale = (locale: Locale) => (locale === 'pl' ? 'pl-PL' : 'en-GB');

export function formatNumber(value: number, locale: Locale, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(intlLocale(locale), opts).format(value);
}

export function formatCurrency(value: number, locale: Locale, opts: { currency?: string; compact?: boolean } = {}): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: opts.currency ?? 'PLN',
    maximumFractionDigits: opts.compact ? 1 : 0,
    notation: opts.compact ? 'compact' : 'standard',
  }).format(value);
}

export function formatPercent(value: number, locale: Locale, digits = 1, signed = false): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    maximumFractionDigits: digits,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(value / 100);
}

export function initials(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => p?.trim()[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * PLN amount shown as "12 345 zł" (pl) / "12,345 zł" (en). `compact: 'auto'` switches to "1,2 mln zł" from 1 M up.
 */
export function formatMoney(value: number, locale: Locale, opts: { compact?: boolean | 'auto'; fractionDigits?: number } = {}): string {
  const compact = opts.compact === 'auto' ? Math.abs(value) >= 1_000_000 : !!opts.compact;
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: 'PLN',
    currencyDisplay: 'narrowSymbol',
    notation: compact ? 'compact' : 'standard',
    // 'always' (ES2023) keeps "4 815 zł" grouped in pl; older lib typings only know boolean.
    useGrouping: (compact ? 'auto' : 'always') as unknown as boolean,
    maximumFractionDigits: compact ? 1 : (opts.fractionDigits ?? 0),
    minimumFractionDigits: compact ? 0 : (opts.fractionDigits ?? 0),
  }).format(value);
}

/** Signed percentage from a number already in percent: 24.2 → "+24,2%". */
export function formatSignedPercent(value: number, locale: Locale, digits = 1): string {
  return formatPercent(value, locale, digits, true).replace('−', '-');
}

/** "45 dni" / "1 dzień" — correct plural forms via Intl. */
export function formatDays(days: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale), { style: 'unit', unit: 'day', unitDisplay: 'long' }).format(days);
}

/** "3 dni temu" / "wczoraj" / "dzisiaj". */
export function formatDaysAgo(days: number, locale: Locale): string {
  return new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' }).format(-days, 'day');
}

/** "2026-03" → "mar 2026" (short) or "marzec 2026" (long). */
export function formatMonthKey(key: string, locale: Locale, long = false): string {
  return formatDate(`${key}-01`, locale, long ? 'LLLL yyyy' : 'LLL yyyy');
}

/** Signed plain number: 1.234 → "+1,2". */
export function formatSignedNumber(value: number, locale: Locale, digits = 1): string {
  return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: digits, signDisplay: 'exceptZero' }).format(value);
}

/** Like `formatPercent`, but always shows exactly `digits` decimals (aligned columns: "22,0%"). */
export function formatPercentFixed(value: number, locale: Locale, digits = 1): string {
  return new Intl.NumberFormat(intlLocale(locale), { style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value / 100);
}
